import { useEffect, useState } from "react";
import { Flame, AlertTriangle, Shield, FileText, ShieldCheck } from "lucide-react";
import { Link } from "react-router-dom";

export default function Footer() {
  const [cfg, setCfg] = useState(null);
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/config`)
      .then((r) => r.json()).then(setCfg).catch(() => {});
  }, []);

  const auditFirm = cfg?.external_audit_firm;
  const auditReport = cfg?.external_audit_report_url;
  const hasExternal = !!(auditFirm && auditReport);

  return (
    <footer className="border-t border-[#2A2E45] bg-[#0F101A]/80 mt-auto">
      <div className="max-w-7xl mx-auto px-4 py-6 text-sm text-slate-400">
        <div className="rounded-xl border border-amber-500/30 bg-amber-950/20 p-4 flex gap-3" data-testid="risk-disclaimer-banner">
          <AlertTriangle size={18} className="text-amber-400 shrink-0 mt-0.5" />
          <div>
            <div className="text-amber-200 font-semibold text-xs uppercase tracking-wider">Risk Disclaimer</div>
            <div className="text-slate-300 text-sm mt-1 leading-relaxed">
              FLAME is an experimental token with <b>no guaranteed value</b>. Burning CRO is irreversible.
              Playing involves risk of total loss of credits used. This is not a bank, exchange, or regulated financial
              product. Must be 18+ and legal in your jurisdiction. See{" "}
              <Link to="/terms" className="underline text-cyan-400">Terms</Link> and{" "}
              <Link to="/privacy" className="underline text-cyan-400">Privacy</Link>.
            </div>
          </div>
        </div>

        <div className="mt-4 rounded-xl border border-emerald-500/30 bg-emerald-950/20 p-4 flex gap-3" data-testid="audit-badge">
          <ShieldCheck size={18} className={`shrink-0 mt-0.5 ${hasExternal ? "text-emerald-300" : "text-amber-300"}`} />
          <div className="flex-1 text-sm">
            {hasExternal ? (
              <>
                <div className="text-emerald-200 font-semibold text-xs uppercase tracking-wider">Audited by {auditFirm}</div>
                <div className="text-slate-300 mt-1">
                  Full audit report: <a href={auditReport} target="_blank" rel="noreferrer" className="underline text-cyan-400">Report ↗</a>.
                  See verified source on the <Link to="/token" className="underline text-cyan-400">token page</Link>.
                </div>
              </>
            ) : (
              <>
                <div className="text-amber-200 font-semibold text-xs uppercase tracking-wider">Internal security review complete · External audit in progress</div>
                <div className="text-slate-300 mt-1">
                  All three contracts (<code>Flame</code>, <code>CROBurnSplitter</code>, <code>RewardsClaim</code>) passed internal pre-audit —
                  14/14 Hardhat tests, 8/8 Foundry fuzz tests across 4,096 runs, Slither static analysis with 0 High/Medium findings.
                  Full report in-repo at <code>/app/contracts/INTERNAL_AUDIT.md</code>.
                  External auditor engagement in progress; this badge auto-updates when the final report is public.
                </div>
              </>
            )}
          </div>
        </div>

        <div className="mt-6 flex flex-wrap gap-6 items-center justify-between">
          <div className="flex items-center gap-2">
            <Flame className="text-orange-500" size={16} />
            <span className="font-display font-bold">FLAME Farm & Arcade</span>
            <span className="text-xs text-slate-500">· Not legal, financial, or tax advice</span>
          </div>
          <div className="flex gap-4 text-xs">
            <Link to="/terms" className="hover:text-white flex items-center gap-1" data-testid="footer-terms"><FileText size={12} /> Terms</Link>
            <Link to="/privacy" className="hover:text-white flex items-center gap-1" data-testid="footer-privacy"><Shield size={12} /> Privacy</Link>
            <Link to="/whitepaper" className="hover:text-white" data-testid="footer-whitepaper">White Paper</Link>
            <Link to="/token" className="hover:text-white" data-testid="footer-token">Token</Link>
            <a href="https://explorer.cronos.org/" target="_blank" rel="noreferrer" className="hover:text-white">Cronos Explorer ↗</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
